import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../config.js';
import { useAuth } from '../context/AuthContext.js';
import AthleteFeedbackView from './AthleteFeedbackView.js';

/**
 * ATHLETE HOME — self-view for older athletes with their own login.
 * Resolves the athlete's own roster record (/api/athlete/me) then shows their
 * schedule + upcoming, videos + coach feedback, and a daily readiness check-in.
 */
function AthleteHome() {
  const { user } = useAuth();
  const auth = { headers: { Authorization: `Bearer ${user?.token}` } };

  const [me, setMe] = useState(null);
  const [err, setErr] = useState('');
  const [schedule, setSchedule] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [pinnedGoals, setPinnedGoals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/athlete/me`, auth);
        setMe(res.data);
      } catch (e) {
        setErr(e.response?.data?.error || 'Could not load your athlete profile.');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!me?.id) return;
    axios.get(`${API_BASE_URL}/api/athlete/${me.id}/weekly-schedule`, auth)
      .then(r => setSchedule(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
    const start = new Date(); start.setHours(0, 0, 0, 0);
    const end = new Date(); end.setMonth(end.getMonth() + 2);
    axios.get(`${API_BASE_URL}/api/calendar/events?start_date=${start.toISOString()}&end_date=${end.toISOString()}`, auth)
      .then(r => setUpcoming(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
    // Pinned, active goals — the SMARTEST "Exhibit": keep them in front of you.
    axios.get(`${API_BASE_URL}/api/v1/goals`, auth)
      .then(r => setPinnedGoals((Array.isArray(r.data) ? r.data : [])
        .filter(g => g.pinned && g.status !== 'achieved' && g.status !== 'exceeded')
        .slice(0, 4)))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  const hhmm = (t) => (t ? String(t).slice(0, 5) : '');
  const fmtEventTime = (e) => {
    const dt = e.start_datetime || e.start_time || e.event_date;
    return dt ? new Date(dt).toLocaleString() : '';
  };

  const section = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginBottom: 16 };

  if (loading) return <div style={{ padding: 24 }}>Loading…</div>;

  if (err) {
    return (
      <div style={{ maxWidth: 720, margin: '40px auto', padding: 20, textAlign: 'center' }}>
        <h1>🤸 My Gym</h1>
        <div style={section}>
          <p>{err}</p>
          <p style={{ color: '#888' }}>Your gym admin can link your login from the Athlete Accounts screen.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h1>🤸 Hi {me?.name?.split(' ')[0] || 'there'}!</h1>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {me?.id && <Link to={`/athlete-card/${me.id}`} className="btn btn-secondary">📇 My Card</Link>}
          <Link to="/wellness/checkin" className="btn btn-primary">✅ Daily Check-In</Link>
        </div>
      </div>

      {/* Pinned goals — kept in front of you (SMARTEST "Exhibit") */}
      {pinnedGoals.length > 0 && (
        <div style={section}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <h2 style={{ margin: 0 }}>🎯 My Goals</h2>
            <Link to="/goals" style={{ fontSize: '.85rem', color: '#4f46e5' }}>All goals →</Link>
          </div>
          <div style={{ display: 'grid', gap: 10 }}>
            {pinnedGoals.map(g => (
              <Link key={g.id} to="/goals" style={{ textDecoration: 'none', color: 'inherit', border: '1px solid #eef2f7', borderRadius: 10, padding: '10px 12px', display: 'block' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
                  <strong style={{ color: '#111827' }}>{g.title}</strong>
                  <span style={{ fontSize: '.8rem', color: '#6b7280', whiteSpace: 'nowrap' }}>{g.progress_percentage || 0}%</span>
                </div>
                {(g.start_value || g.target_value) && (
                  <div style={{ fontSize: '.82rem', color: '#475569', marginTop: 2 }}>{g.current_value ?? g.start_value ?? '—'} → {g.target_value ?? '—'} {g.unit || ''}</div>
                )}
                <div style={{ height: 6, borderRadius: 999, background: '#eef2ff', marginTop: 6, overflow: 'hidden' }}>
                  <div style={{ width: `${g.progress_percentage || 0}%`, height: '100%', background: '#4f46e5', borderRadius: 999 }} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Schedule */}
      <div style={section}>
        <h2 style={{ marginTop: 0 }}>📅 My Schedule</h2>
        {schedule.length === 0 ? (
          <p style={{ color: '#888' }}>No classes scheduled.</p>
        ) : (
          <table className="data-table" style={{ width: '100%' }}>
            <thead><tr><th>Class</th><th>Day</th><th>Time</th></tr></thead>
            <tbody>
              {schedule.map((s, i) => (
                <tr key={s.id || i}>
                  <td>{s.class_name || s.name || '-'}</td>
                  <td>{s.day_of_week || s.day || '-'}</td>
                  <td>{s.start_time ? `${hhmm(s.start_time)}–${hhmm(s.end_time)}` : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Upcoming */}
      <div style={section}>
        <h2 style={{ marginTop: 0 }}>⏭️ Upcoming ({upcoming.length})</h2>
        {upcoming.length === 0 ? (
          <p style={{ color: '#888' }}>Nothing scheduled yet.</p>
        ) : (
          <table className="data-table" style={{ width: '100%' }}>
            <thead><tr><th>Event</th><th>Type</th><th>When</th><th>Where</th></tr></thead>
            <tbody>
              {upcoming.slice(0, 15).map(e => (
                <tr key={e.id}>
                  <td>{e.title || e.event_name}</td>
                  <td>{(e.event_type || 'event').replace(/_/g, ' ')}</td>
                  <td>{fmtEventTime(e)}</td>
                  <td>{e.location_details || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Videos + coach feedback */}
      <div style={section}>
        <h2 style={{ marginTop: 0 }}>🎥 My Videos & Coach Feedback</h2>
        {me?.id && <AthleteFeedbackView athleteId={me.id} athleteName={me.name} />}
      </div>
    </div>
  );
}

export default AthleteHome;
